import { useState, useCallback, useEffect, useRef } from "react";

interface AdminRangeSelectorProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  step?: number;
  className?: string;
}

export function AdminRangeSelector({
  min,
  max,
  value,
  onChange,
  step = 1,
  className = "",
}: AdminRangeSelectorProps) {
  const [isDragging, setIsDragging] = useState<"min" | "max" | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const formatValue = useCallback((val: number) => {
    if (val >= 1000000) {
      return `$${(val / 1000000).toFixed(1)}M`;
    } else if (val >= 1000) {
      return `$${(val / 1000).toFixed(0)}K`;
    }
    return `$${val}`;
  }, []);

  const handleInputChange = useCallback(
    (index: 0 | 1, newValue: number) => {
      const updatedValue: [number, number] = [...value];
      updatedValue[index] = Math.max(min, Math.min(max, newValue));

      // Ensure min <= max
      if (index === 0 && updatedValue[0] > updatedValue[1]) {
        updatedValue[1] = updatedValue[0];
      } else if (index === 1 && updatedValue[1] < updatedValue[0]) {
        updatedValue[0] = updatedValue[1];
      }

      onChange(updatedValue);
    },
    [value, onChange, min, max]
  );

  const getPercentage = useCallback(
    (val: number) => ((val - min) / (max - min)) * 100,
    [min, max]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(
        0,
        Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
      );
      const newValue = min + (percentage / 100) * (max - min);
      const roundedValue = Math.round(newValue / step) * step;

      if (isDragging === "min") {
        handleInputChange(0, Math.min(roundedValue, value[1]));
      } else if (isDragging === "max") {
        handleInputChange(1, Math.max(roundedValue, value[0]));
      }
    },
    [isDragging, min, max, step, value, handleInputChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className={`w-full ${className}`}>
      {/* Range Display */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs font-semibold text-muted-foreground">
          {step === 1 ? value[0] : formatValue(value[0])}
        </span>
        <span className="text-xs text-muted-foreground font-medium">to</span>
        <span className="text-xs font-semibold text-muted-foreground">
          {step === 1 ? value[1] : formatValue(value[1])}
        </span>
      </div>

      {/* Range Slider */}
      <div className="relative mb-3" ref={sliderRef}>
        {/* Track */}
        <div className="w-full h-1.5 bg-muted rounded-full relative">
          {/* Active Range */}
          <div
            className="absolute h-1.5 rounded-full transition-all duration-200"
            style={{
              backgroundColor: "#d7785b",
              left: `${getPercentage(value[0])}%`,
              width: `${getPercentage(value[1]) - getPercentage(value[0])}%`,
            }}
          />

          {/* Min Thumb */}
          <div
            className={`absolute w-4 h-4 rounded-full border-2 border-white shadow-md cursor-pointer transition-all duration-200 select-none ${
              isDragging === "min" ? "scale-110 shadow-lg" : "hover:scale-105"
            }`}
            style={{
              backgroundColor: "#d7785b",
              left: `${getPercentage(value[0])}%`,
              top: "-5px",
              transform: "translateX(-50%)",
              zIndex: isDragging === "min" ? 20 : 10,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging("min");
            }}
          />

          {/* Max Thumb */}
          <div
            className={`absolute w-4 h-4 rounded-full border-2 border-white shadow-md cursor-pointer transition-all duration-200 select-none ${
              isDragging === "max" ? "scale-110 shadow-lg" : "hover:scale-105"
            }`}
            style={{
              backgroundColor: "#d7785b",
              left: `${getPercentage(value[1])}%`,
              top: "-5px",
              transform: "translateX(-50%)",
              zIndex: isDragging === "max" ? 20 : 10,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging("max");
            }}
          />
        </div>
      </div>

      {/* Input Fields */}
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1 font-medium">
            Min
          </label>
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value[0]}
            onChange={(e) =>
              handleInputChange(0, parseInt(e.target.value) || min)
            }
            className="w-full px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring/30 bg-card text-muted-foreground font-medium"
            onFocus={(e) => {
              e.target.style.borderColor = "#d7785b";
              e.target.style.boxShadow = "0 0 0 2px rgba(215, 120, 91, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#d6d3d1";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1 font-medium">
            Max
          </label>
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value[1]}
            onChange={(e) =>
              handleInputChange(1, parseInt(e.target.value) || max)
            }
            className="w-full px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring/30 bg-card text-muted-foreground font-medium"
            onFocus={(e) => {
              e.target.style.borderColor = "#d7785b";
              e.target.style.boxShadow = "0 0 0 2px rgba(215, 120, 91, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#d6d3d1";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>
    </div>
  );
}
